import { CSSProperties } from 'preact/compat';
import {
  RoughAnnotationType,
  RoughPadding,
  FullPadding,
  RoughAnnotationConfig,
} from 'rough-notation/lib/model';
import {
  LegacyHighlight,
  Selector,
  Cuepoint as AdapterCuepoint,
} from '@libertify.frontend/adapters';
type Locale = 'fr-FR' | 'en-US';
type Translations = Record<Locale, string>;

export type WidgetPublicConfig = {
  // Those values are stored on the window
  product_sku: string;
  variation_id?: string;
  language: string;
  app_id: string;
  widget_base: string;
  datasource_base?: string;
  organization_id: string;
  debug: boolean;
};

type WigdetStyles = Pick<CSSProperties, 'width' | 'height' | 'background'> & {
  font_family?: string;
  position_bottom?: number;
  min_y?: number;
  min_x?: number;
  start_y?: number;
  start_x?: number;
  max_y_from_bottom?: number; // Offset from the bottom of the screen
  overflow?: string;
  width?: string;
  height?: string;
};

// The part of the configuration that is shared between the widget and the editor
export type BaseConfig = {
  debug?: boolean;
  alt_config_url?: string;
  alt_highlights_url?: string;
  page_id?: string;
  colors?: {
    play_button_bg?: string;
    play_button?: string;
    primary: string;
    secondary: string;
    gradient: string[] | string;
  };
  styles?: {
    max_zindex?: number;
    root_styles?: string;
    media?: CSSProperties[];
    image?: CSSProperties[];
    widget?: {
      small?: WigdetStyles;
      medium?: WigdetStyles;
      large?: WigdetStyles;
    } & WigdetStyles;
    widget_controls?: CSSProperties;
    play_button?: WigdetStyles;
  };
  breakpoints?: {
    small?: number;
    medium?: number;
    large?: number;
  };
  static_image_url?: string; // URL for static image to display with audio
  video_thumbnail_url?: string; // URL for video thumbnail to display (useful when no video poster available on Safari)
  assets?: string[]; // URL for video assets
  components?: {
    controls?: {
      enabled: boolean;
    };
    powered_by?: Translations & Breakpoints;
    powered_by_brand?: string;
    powered_by_link?: string;
    bubble?: {
      title: Translations;
      subtitle?: Translations;
    };
    play_button?: Translations;
    progress_bar?: { enabled: boolean };
  };
  analytics_enabled?: boolean;
  actions?: Actions;
  bracket_override_enabled?: boolean;
  features?: {
    scroll_to_bottom_at_start?: boolean;
  };
};

type WidgetMediaConfig = {
  // URL for media to display
  // can be multiple URLs for different variations (legacy config files use case)
  media?: string | string[];
};
export type WidgetType = 'video' | 'visualizer';

type WidgetOnlyConfig = {
  variation_id: string;
  media_url: string;

  media_type?: 'video' | 'audio';
  widget_type?: WidgetType;
};
export type WidgetConfig = WidgetPublicConfig &
  BaseConfig &
  WidgetOnlyConfig &
  WidgetMediaConfig;

export type THighlight = {
  id: string;
  generated?: boolean;
  nInSeries?: number;
  fillColor?: string;
  original_id?: string;
  counter?: number;
  content?: { script_text?: string | null; text?: string | null };
  selector: Selector;
  selectors?: { start: string; text?: string | string[] }[];
  annotation_options?: {
    animate: boolean;
    type: RoughAnnotationType;
    protected_type?: boolean; // Do not override type if true
    color: string;
    fillColor?: string;
    stroke_width: number;
    padding: RoughPadding | FullPadding;
  } & RoughAnnotationConfig;
  inline?: boolean;
  time?: number;
  annotationOptions?: THighlight['annotation_options'];
};
export interface DataRetrievalStrategy {
  retrieveData(liveData?: any): Promise<{
    highlights: LegacyHighlight[];
    config: WidgetConfig;
    assets?: unknown[];
    cuepoints: AdapterCuepoint[];
    publication: Publication | null;
  }>;
  getMedia(): string;
  getMediaStyle(): CSSProperties;
}

export interface GlobalContext {
  config?: WidgetConfig | null;
}

export interface Cuepoint extends Omit<AdapterCuepoint, 'rough_notation'> {
  rough_notation: {
    animate: boolean;
    type: RoughAnnotationType;
    animation_delay: number;
    animation_duration: number;
    stroke_width: number;
    padding: number;
    color: string;
    brackets: RoughAnnotationConfig['brackets'];
    iterations: number;
    multiline: boolean;
    show: boolean;
  };
  page: number;
  weight: number;
}
export enum ActionName {
  REMOVE_HIGHLIGHTS = 'REMOVE_HIGHLIGHTS',
}
enum ActionPhase {
  init = 'INIT',
  loading = 'LOADING',
  loaded = 'LOADED',
  error = 'ERROR',
  ended = 'END_OF_MEDIA',
}

export type Actions = Partial<Record<ActionPhase, ActionName[]>>;
export type BoundActions = Partial<
  Record<ActionPhase, EventListenerOrEventListenerObject[]>
>;

type Breakpoints = {
  small?: CSSStyleDeclaration;
  medium?: CSSStyleDeclaration;
  large?: CSSStyleDeclaration;
};
export type Publication = {
  media: {
    url: string;
    id: string;
    status: string;
  };
  project_id: string;
  instance_id: string;
};

export type LiveData = {
  highlights: LegacyHighlight[];
  config: WidgetConfig;
  assets?: unknown[];
  cuepoints: AdapterCuepoint[];
  publication: Publication | null;
};
